/* eslint-disable react/prop-types */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';

import Footer from 'Component/Footer';
import InstallPrompt from 'Component/InstallPrompt';
import { DEFAULT_STATE_NAME } from 'Component/NavigationAbstract/NavigationAbstract.config';
import CmsPage from 'Route/CmsPage';
import { changeNavigationState } from 'Store/Navigation/Navigation.action';
import { TOP_NAVIGATION_TYPE } from 'Store/Navigation/Navigation.reducer';
import { LocationType, MatchType } from 'Type/Router.type';

import './HomePage.style';

/** @namespace Pwa/Route/HomePage/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    pageIdentifiers: state.ConfigReducer.cms_home_page,
    device: state.ConfigReducer.device
});

/** @namespace Pwa/Route/HomePage/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    changeHeaderState: (state) => dispatch(changeNavigationState(TOP_NAVIGATION_TYPE, state))
});

/** @namespace Pwa/Route/HomePage/Container */
export class HomePageContainer extends PureComponent {
    static propTypes = {
        changeHeaderState: PropTypes.func.isRequired,
        pageIdentifiers: PropTypes.string.isRequired,
        location: LocationType.isRequired,
        match: MatchType.isRequired
    };

    componentDidMount() {
        const { changeHeaderState } = this.props;

        changeHeaderState({
            name: DEFAULT_STATE_NAME,
            isHiddenOnMobile: false
        });
    }

    containerProps() {
        const {
            changeHeaderState, location, match, pageIdentifiers
        } = this.props;

        return {
            changeHeaderState,
            location,
            match,
            pageIdentifiers
        };
    }

    renderStructuredDataOrganization() {
        const structuredData = `{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Asgard Sp. z o.o.",
  "url": "https://bluecollection.gifts/",
  "logo": "https://bluecollection.gifts/media/logo/default/blue_collection.jpg",
  "description": "Dostawca wysokiej jakości gadżetów promocyjnych i upominków firmowych z nadrukiem.",
  "image": "https://bluecollection.gifts/media/logo/default/blue_collection.jpg",
  "sameAs": [
    "https://www.instagram.com/bluecollection.gifts/",
    "https://www.facebook.com/bluecollection.gifts",
    "https://www.youtube.com/@BLUECOLLECTIONvideogifts",
    "https://www.linkedin.com/company/asgard-sp-z-o-o/posts/?feedView=all"
  ],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "ul. Rolna 17",
    "addressLocality": "Baranowo",
    "postalCode": "62-081",
    "addressCountry": "PL"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+48 61 840 07 40",
    "email": "biuro@bluecollection.gifts",
    "contactType": "customer service",
    "availableLanguage": ["pl", "en", "de", "fr", "cs"]
  },
  "department": [
    {
      "@type": "Organization",
      "name": "Dział Sprzedaży",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+48 61 840 07 40",
        "email": "sprzedaz@bluecollection.gifts",
        "contactType": "sales",
        "availableLanguage": ["pl", "en", "de", "fr", "cs"]
      }
    },
    {
      "@type": "Organization",
      "name": "Obsługa Klienta",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+48 61 840 07 40",
        "email": "obsluga@bluecollection.gifts",
        "contactType": "customer service",
        "availableLanguage": ["pl", "en", "de", "fr", "cs"]
      }
    }
  ]
}`;

        return (
            <script type="application/ld+json">
                { structuredData }
            </script>
        );
    }

    renderStructuredDataWebsite() {
        const structuredData = `{
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "BLUE COLLECTION",
  "url": "https://bluecollection.gifts/",
  "description": "Dostawca wysokiej jakości gadżetów promocyjnych i upominków firmowych z nadrukiem.",
  "image": "https://bluecollection.gifts/media/logo/default/blue_collection.jpg",
  "inLanguage": ["pl", "en", "de", "fr", "cs"],
  "sameAs": [
    "https://www.instagram.com/bluecollection.gifts/",
    "https://www.facebook.com/bluecollection.gifts",
    "https://www.youtube.com/@BLUECOLLECTIONvideogifts",
    "https://www.linkedin.com/company/asgard-sp-z-o-o/posts/?feedView=all"
  ],
  "publisher": {
    "@type": "Organization",
    "name": "Asgard Sp. z o.o.",
    "logo": "https://bluecollection.gifts/media/logo/default/blue_collection.jpg",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "ul. Rolna 17",
      "addressLocality": "Baranowo",
      "postalCode": "62-081",
      "addressCountry": "PL"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+48 61 840 07 40",
      "email": "biuro@bluecollection.gifts",
      "contactType": "customer service",
      "availableLanguage": ["pl", "en", "de", "fr", "cs"]
    }
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://bluecollection.gifts/pl/catalogsearch/result/?q={search_term_string}",
    "query-input": "required name=search_term_string"
  },
  "mainEntity": [
    {
      "@type": "WebPage",
      "name": "Nowości i Promocje",
      "url": "https://bluecollection.gifts/pl/nowosci-i-promocje.html",
      "inLanguage": ["pl", "en", "de", "fr", "cs"]
    },
    {
      "@type": "WebPage",
      "name": "Top Seller",
      "url": "https://bluecollection.gifts/pl/nowosci-i-promocje/top-seller.html",
      "inLanguage": ["pl", "en", "de", "fr", "cs"]
    }
  ]
}`;

        return (
            <script type="application/ld+json">
                { structuredData }
            </script>
        );
    }

    render() {
        const {
            device: { isMobile }
        } = this.props;

        return (
            <div block="HomePage" mods={ { isMobile } }>
                <InstallPrompt />
                { this.renderStructuredDataOrganization() }
                { this.renderStructuredDataWebsite() }
                <CmsPage { ...this.containerProps() } />
                <Footer isVisibleOnMobile />
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(HomePageContainer);
